/**
 * API Client
 *
 * Centralized HTTP client with:
 * - Automatic Authorization header injection
 * - Token refresh on 401 (with queue to prevent race conditions)
 * - Response unwrapping (extracts data from APIResponse)
 * - Standardized error handling
 */

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { API_URL, REQUEST_TIMEOUT, TOKEN_KEYS } from '../config/api';

// =============================================================================
// Types
// =============================================================================

/**
 * Standard API success response from backend
 */
export interface APIResponse<T = unknown> {
  success: true;
  data: T;
  message?: string;
  timestamp: string;
}

/**
 * Standard API error response from backend
 */
export interface APIErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown> | unknown[];
  };
  timestamp: string;
  request_id?: string;
}

/**
 * Paginated response wrapper
 */
export interface PaginatedResponse<T> extends APIResponse<T[]> {
  pagination: {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

/**
 * Custom API Error class
 */
export class ApiError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly details?: Record<string, unknown> | unknown[];
  public readonly requestId?: string;
  public readonly timestamp: string;

  constructor(
    message: string,
    code: string,
    status: number,
    details?: Record<string, unknown> | unknown[],
    requestId?: string,
    timestamp?: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
    this.requestId = requestId;
    this.timestamp = timestamp || new Date().toISOString();
  }

  static fromResponse(response: APIErrorResponse, status: number): ApiError {
    return new ApiError(
      response.error.message,
      response.error.code,
      status,
      response.error.details,
      response.request_id,
      response.timestamp
    );
  }
}

// =============================================================================
// Token Storage
// =============================================================================

class TokenStorage {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  constructor() {
    // Load tokens from localStorage on init
    if (typeof window !== 'undefined') {
      this.accessToken = localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
      this.refreshToken = localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
    }
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  setTokens(accessToken: string, refreshToken: string): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;

    if (typeof window !== 'undefined') {
      localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
      localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
    }
  }

  clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;

    if (typeof window !== 'undefined') {
      localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(TOKEN_KEYS.USER);
    }
  }

  hasTokens(): boolean {
    return !!this.accessToken && !!this.refreshToken;
  }
}

export const tokenStorage = new TokenStorage();

// =============================================================================
// Refresh Token Queue
// =============================================================================

type QueuedRequest = {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
};

let isRefreshing = false;
let refreshQueue: QueuedRequest[] = [];

const processQueue = (error: Error | null, token: string | null = null): void => {
  refreshQueue.forEach((request) => {
    if (error) {
      request.reject(error);
    } else if (token) {
      request.resolve(token);
    }
  });
  refreshQueue = [];
};

// =============================================================================
// Axios Instance
// =============================================================================

const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// =============================================================================
// Request Interceptor
// =============================================================================

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = tokenStorage.getAccessToken();

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// =============================================================================
// Response Interceptor
// =============================================================================

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Unwrap successful responses
    const data = response.data;

    // If response has our standard format, unwrap it
    if (data && typeof data === 'object' && 'success' in data) {
      if (data.success === true) {
        // Return the full response for paginated endpoints
        if ('pagination' in data) {
          return response;
        }
        // For regular responses, we still return full response
        // The caller can access .data.data
        return response;
      } else {
        // Handle error response format
        const errorData = data as APIErrorResponse;
        throw ApiError.fromResponse(errorData, response.status);
      }
    }

    return response;
  },
  async (error: AxiosError<APIErrorResponse>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry auth endpoints (login/refresh) — pass their errors through directly
      if (
        originalRequest.url?.includes('/auth/refresh') ||
        originalRequest.url?.includes('/auth/login')
      ) {
        if (originalRequest.url?.includes('/auth/refresh')) {
          tokenStorage.clearTokens();
          window.dispatchEvent(new CustomEvent('auth:logout'));
        }
        // Re-throw the real backend error so callers can display it
        if (error.response?.data) {
          const errorData = error.response.data as APIErrorResponse;
          if (errorData.success === false) {
            return Promise.reject(ApiError.fromResponse(errorData, error.response.status));
          }
        }
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request until refresh completes
        return new Promise<string>((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = tokenStorage.getRefreshToken();

      if (!refreshToken) {
        isRefreshing = false;
        tokenStorage.clearTokens();
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return Promise.reject(new ApiError('No refresh token', 'NO_REFRESH_TOKEN', 401));
      }

      try {
        // Make refresh request without interceptors to avoid loops
        const response = await axios.post<APIResponse<{
          access_token: string;
          refresh_token: string;
        }>>(
          `${API_URL}/auth/refresh`,
          { refresh_token: refreshToken },
          {
            headers: { 'Content-Type': 'application/json' },
          }
        );

        if (response.data.success && response.data.data) {
          const { access_token, refresh_token } = response.data.data;
          tokenStorage.setTokens(access_token, refresh_token);

          // Update original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
          }

          // Process queued requests
          processQueue(null, access_token);
          isRefreshing = false;

          return apiClient(originalRequest);
        } else {
          throw new Error('Refresh failed');
        }
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        isRefreshing = false;
        tokenStorage.clearTokens();
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return Promise.reject(refreshError);
      }
    }

    // Handle 403 Forbidden (RBAC)
    if (error.response?.status === 403) {
      const errorData = error.response.data as any;
      const detail = errorData?.detail || errorData?.error?.message || '';
      const message = detail || 'Accès non autorisé. Vous n\'avez pas les permissions nécessaires.';
      // Emit global event so toast system can display it
      window.dispatchEvent(new CustomEvent('api:forbidden', { detail: message }));
      throw new ApiError(message, 'FORBIDDEN', 403);
    }

    // Handle other errors
    if (error.response?.data) {
      const errorData = error.response.data;
      if (errorData.success === false) {
        throw ApiError.fromResponse(errorData, error.response.status);
      }
    }

    // Network error or other
    if (!error.response) {
      throw new ApiError(
        'Network error. Please check your connection.',
        'NETWORK_ERROR',
        0
      );
    }

    throw new ApiError(
      error.message || 'An unexpected error occurred',
      'UNKNOWN_ERROR',
      error.response?.status || 500
    );
  }
);

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Extract data from API response
 */
export function unwrapResponse<T>(response: AxiosResponse<APIResponse<T>>): T {
  return response.data.data;
}

/**
 * Extract paginated data from API response
 */
export function unwrapPaginatedResponse<T>(
  response: AxiosResponse<PaginatedResponse<T>>
): {
  data: T[];
  pagination: PaginatedResponse<T>['pagination'];
} {
  return {
    data: response.data.data,
    pagination: response.data.pagination,
  };
}

/**
 * Type guard to check if error is ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Get error message from any error type
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

// =============================================================================
// Export
// =============================================================================

export default apiClient;
